import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import BrandShell from "../components/BrandShell";
import { useAuth } from "../auth/AuthContext";
import { ArrowRight, Loader2 } from "lucide-react";
import { PRICING } from "../data/pricing";

export default function Signup() {
  useEffect(() => { document.title = "Create Your Account | JC3 Wealth Legacy Group"; }, []);
  const { signup } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    first_name: "", last_name: "", email: "", phone: "",
    city_state: "", service_package: PRICING[0].name,
    referral_code: "", password: "", confirm: "",
    date_of_birth: "", ssn: "", street_address: "", zip_code: "",
  });
  const [consent, setConsent] = useState(false);
  const [glbaAck, setGlbaAck] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [state, setState] = useState({ status: "idle", error: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!consent) {
      setState({ status: "error", error: "Please acknowledge our consulting-services disclosure to continue." });
      return;
    }
    if (!glbaAck) {
      setState({ status: "error", error: "Please acknowledge receipt of our Financial Privacy Notice to continue." });
      return;
    }
    if (form.password !== form.confirm) {
      setState({ status: "error", error: "Passwords do not match." });
      return;
    }
    if (form.password.length < 8) {
      setState({ status: "error", error: "Password must be at least 8 characters." });
      return;
    }
    setState({ status: "loading", error: "" });
    try {
      const { confirm, ...payload } = form;
      const u = await signup({
        ...payload,
        referral_code: payload.referral_code || null,
        consent_given: true,
        glba_acknowledged: true,
        sms_consent: smsConsent,
      });
      nav(u.role === "admin" ? "/dashboard/admin" : "/dashboard/client", { replace: true });
    } catch (err) {
      // Map common 400s to actionable messages so testers know exactly what to do
      const raw = (err.message || "Sign-up failed.").toString();
      let friendly = raw;
      if (/already exists/i.test(raw)) {
        friendly = "An account with this email already exists. Try signing in instead — link below.";
      } else if (/consulting-services disclosure|acknowledg/i.test(raw)) {
        friendly = "Please check the consent box above the submit button to continue.";
      } else if (/request failed/i.test(raw) || /something went wrong/i.test(raw)) {
        friendly = "We couldn't reach the server. Check your internet connection and try again.";
      }
      setState({ status: "error", error: friendly });
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-jc-gold/30 px-0 py-3 font-body text-base text-jc-white placeholder-jc-muted focus:border-jc-gold focus:outline-none transition-colors";
  const labelCls = "font-mono text-[10px] uppercase tracking-[0.28em] text-jc-gold";

  return (
    <BrandShell dashboardChrome>
      <section data-testid="signup-page" className="relative bg-jc-black text-jc-white min-h-[calc(100vh-5rem)] py-16 overflow-hidden">
        <div className="absolute inset-0 jc-grid-bg" />
        <div className="relative z-10 mx-auto w-full max-w-2xl px-6 md:px-10">
          <div className="flex items-center gap-3 justify-center mb-10">
            <span className="inline-block h-8 w-8 border border-jc-gold rotate-45" />
            <span className="font-display text-xl tracking-[0.18em] text-jc-white">JC3 <span className="text-jc-gold">·</span> WEALTH LEGACY</span>
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-jc-gold text-center mb-3">◆ New Client Registration</p>
          <h1 data-testid="signup-heading" className="font-display text-jc-white text-center" style={{ fontSize: "clamp(2.2rem, 5vw, 3.4rem)", lineHeight: 0.95 }}>
            CREATE YOUR <span className="text-jc-gold">ACCOUNT.</span>
          </h1>

          <form onSubmit={onSubmit} className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-5" data-testid="signup-form">
            <div><label className={labelCls}>First Name</label><input data-testid="signup-first-name" required name="first_name" value={form.first_name} onChange={onChange} className={inputCls} placeholder="Jane" /></div>
            <div><label className={labelCls}>Last Name</label><input data-testid="signup-last-name" required name="last_name" value={form.last_name} onChange={onChange} className={inputCls} placeholder="Smith" /></div>
            <div><label className={labelCls}>Email</label><input data-testid="signup-email" required type="email" name="email" value={form.email} onChange={onChange} className={inputCls} placeholder="you@example.com" /></div>
            <div><label className={labelCls}>Phone</label><input data-testid="signup-phone" required name="phone" value={form.phone} onChange={onChange} className={inputCls} placeholder="(555) 555-5555" /></div>
            <div className="sm:col-span-2"><label className={labelCls}>City & State</label><input data-testid="signup-city-state" required name="city_state" value={form.city_state} onChange={onChange} className={inputCls} placeholder="Atlanta, GA" /></div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Service</label>
              <select
                data-testid="signup-package"
                name="service_package"
                value={form.service_package}
                onChange={onChange}
                className={`${inputCls} appearance-none cursor-pointer`}
                style={{ backgroundImage: "linear-gradient(45deg, transparent 50%, #C8A46A 50%), linear-gradient(135deg, #C8A46A 50%, transparent 50%)", backgroundPosition: "calc(100% - 14px) 22px, calc(100% - 8px) 22px", backgroundSize: "6px 6px, 6px 6px", backgroundRepeat: "no-repeat" }}
              >
                {PRICING.map((p) => (
                  <option key={p.name} value={p.name} className="bg-jc-navy text-jc-white">{p.name} — {p.priceLine}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2"><label className={labelCls}>Referral Code (optional)</label><input data-testid="signup-referral" name="referral_code" value={form.referral_code} onChange={onChange} className={inputCls} placeholder="e.g. JC3-XYZ" /></div>

            {/* Extended Intake (Sprint 1: 7A) — required PII */}
            <div className="sm:col-span-2 mt-2 mb-1 pt-3 border-t border-jc-gold/20">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-jc-gold">◆ Verify Your Identity</p>
              <p className="font-body text-[12px] text-jc-muted mt-1">Your Social Security number and date of birth are encrypted. Used only to verify your identity for the services you choose.</p>
            </div>
            <div><label className={labelCls}>Date of Birth</label><input data-testid="signup-dob" required type="date" name="date_of_birth" value={form.date_of_birth} onChange={onChange} className={inputCls} /></div>
            <div><label className={labelCls}>Social Security Number</label><input data-testid="signup-ssn" required type="password" name="ssn" value={form.ssn} onChange={onChange} className={inputCls} placeholder="•••-••-••••" autoComplete="off" maxLength={11} /></div>
            <div className="sm:col-span-2"><label className={labelCls}>Street Address</label><input data-testid="signup-street" required name="street_address" value={form.street_address} onChange={onChange} className={inputCls} placeholder="123 Peachtree St NE" autoComplete="street-address" /></div>
            <div><label className={labelCls}>ZIP Code</label><input data-testid="signup-zip" required name="zip_code" value={form.zip_code} onChange={onChange} className={inputCls} placeholder="30303" autoComplete="postal-code" /></div>
            <div><label className={labelCls}>Password</label><input data-testid="signup-password" required type="password" name="password" value={form.password} onChange={onChange} className={inputCls} placeholder="8+ characters" autoComplete="new-password" /></div>
            <div><label className={labelCls}>Confirm Password</label><input data-testid="signup-confirm" required type="password" name="confirm" value={form.confirm} onChange={onChange} className={inputCls} placeholder="Re-type password" autoComplete="new-password" /></div>

            <div className="sm:col-span-2 mt-6">
              {/* Compliance consent — required */}
              <label
                data-testid="signup-consent-row"
                className="flex items-start gap-3 cursor-pointer select-none mb-4"
              >
                <span className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    data-testid="signup-consent-checkbox"
                    required
                    className="peer appearance-none h-5 w-5 rounded-sm border-2 border-jc-gold/70 bg-transparent checked:bg-jc-navy-2 checked:border-jc-gold focus:outline-none focus:ring-2 focus:ring-jc-gold/40 transition-colors cursor-pointer"
                    style={{ borderColor: consent ? "#C8A46A" : undefined }}
                  />
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 16"
                    className="absolute inset-0 m-auto h-3.5 w-3.5 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
                    fill="none"
                    stroke="#C8A46A"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3,8.5 6.5,12 13,4.5" />
                  </svg>
                </span>
                <span className="font-body text-[13px] leading-[1.6]" style={{ color: "#BDB6A6" }}>
                  I authorize JC3 Wealth Legacy Group LLC to review the information I provide and prepare consulting recommendations for the services I select. I understand JC3 is a financial consulting firm and does not guarantee specific results. See <Link to="/terms" className="text-jc-gold jc-link-underline">consulting services</Link> terms.
                </span>
              </label>

              {/* GLBA Financial Privacy Notice acknowledgment — required */}
              <label
                data-testid="signup-glba-row"
                className="flex items-start gap-3 cursor-pointer select-none mb-4"
              >
                <span className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={glbaAck}
                    onChange={(e) => setGlbaAck(e.target.checked)}
                    data-testid="signup-glba-checkbox"
                    required
                    className="peer appearance-none h-5 w-5 rounded-sm border-2 border-jc-gold/70 bg-transparent checked:bg-jc-navy-2 checked:border-jc-gold focus:outline-none focus:ring-2 focus:ring-jc-gold/40 transition-colors cursor-pointer"
                    style={{ borderColor: glbaAck ? "#C8A46A" : undefined }}
                  />
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 16 16"
                    className="absolute inset-0 m-auto h-3.5 w-3.5 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity"
                    fill="none"
                    stroke="#C8A46A"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="3,8.5 6.5,12 13,4.5" />
                  </svg>
                </span>
                <span className="font-body text-[13px] leading-[1.6]" style={{ color: "#BDB6A6" }}>
                  I acknowledge that I have received and reviewed the JC3 Wealth Legacy Group LLC{" "}
                  <Link to="/privacy/glba" target="_blank" className="text-jc-gold jc-link-underline">Financial Privacy Notice</Link>{" "}
                  (GLBA / Regulation P) describing how my personal information is collected, shared, and protected.
                </span>
              </label>

              {/* SMS/call consent — optional, unchecked by default */}
              <label data-testid="signup-sms-row" className="flex items-start gap-3 cursor-pointer select-none mb-4">
                <span className="relative flex-shrink-0 mt-0.5">
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => setSmsConsent(e.target.checked)}
                    data-testid="signup-sms-checkbox"
                    className="peer appearance-none h-5 w-5 rounded-sm border-2 border-jc-gold/70 bg-transparent checked:bg-jc-navy-2 checked:border-jc-gold focus:outline-none focus:ring-2 focus:ring-jc-gold/40 transition-colors cursor-pointer"
                    style={{ borderColor: smsConsent ? "#C8A46A" : undefined }}
                  />
                  <svg aria-hidden="true" viewBox="0 0 16 16" className="absolute inset-0 m-auto h-3.5 w-3.5 pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" fill="none" stroke="#C8A46A" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3,8.5 6.5,12 13,4.5" />
                  </svg>
                </span>
                <span className="font-body text-[13px] leading-[1.6]" style={{ color: "#BDB6A6" }}>
                  Optional: I agree to receive calls and texts from JC3 Wealth Legacy Group LLC about my account, appointments and services at the number above, including by automated technology. Message and data rates may apply. Reply STOP to opt out. Consent is not a condition of purchase.
                </span>
              </label>

              <button
                type="submit"
                disabled={state.status === "loading" || !consent || !glbaAck}
                data-testid="signup-submit"
                className="jc-btn jc-btn-primary w-full !py-4 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {state.status === "loading" ? (
                  <><Loader2 size={16} className="animate-spin" /> CREATING ACCOUNT...</>
                ) : (
                  <>CREATE MY ACCOUNT <ArrowRight size={16} strokeWidth={2.5} /></>
                )}
              </button>
              {state.status === "error" && (
                <div
                  data-testid="signup-error"
                  role="alert"
                  className="mt-5 px-5 py-4 rounded-md border border-red-400/40 bg-red-500/10 text-red-200 font-body text-sm leading-relaxed text-center"
                >
                  {state.error}
                </div>
              )}
              <p className="mt-6 text-center font-body text-base text-jc-muted">
                Already have an account?{" "}
                <Link to="/dashboard" className="text-jc-gold jc-link-underline">Sign in</Link>
              </p>
            </div>
          </form>
        </div>
      </section>
    </BrandShell>
  );
}
